import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { v4 as newGuid } from "uuid";
import { useSession } from "../../hooks";
import { IssueDto, useCreateProposal } from "../../http";
import { CreateEditProposalForm, ICreateEditProposalFormState } from "./CreateEditProposalForm";

interface IProps {
  visible: boolean;
  issue: IssueDto;
  onCreated: (proposalGuid: string) => void;
  onCancelled: () => void;
}

const CreateProposalModal = (props: IProps) => {
  const user = useSession();
  const { mutate: createProposal, isLoading: isCreatingProposal } = useCreateProposal();

  const { formState, control, handleSubmit, reset } = useForm<ICreateEditProposalFormState>({
    defaultValues: {
      description: "",
      notes: "",
    },
  });

  async function handleCreateClicked() {
    const newProposalGuid = newGuid();

    handleSubmit((data) => {
      createProposal(
        {
          proposalGuid: newProposalGuid,
          issueGuid: props.issue.guid,
          description: data.description,
          notes: data.notes,
          issueCreatedByUser: {
            userId: user.userId,
            profilePhotoSmallUrl: user.profilePhotoSmallUrl,
            displayName: user.displayName,
            enabled: true,
          },
          teamSlug: props.issue.assignedTeam?.slug,
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            props.onCreated(newProposalGuid);
          },
        },
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Proposal</DialogTitle>
      <DialogContent>
        <CreateEditProposalForm formState={formState} control={control} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelClicked} disabled={isCreatingProposal}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateClicked}
          disabled={isCreatingProposal}
          endIcon={isCreatingProposal ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateProposalModal };
